import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authActions, tanqueAction, fazendaAction } from '../_actions';
import { withRouter } from '../_helpers/withRouter';
import NavBar from '../_components/navbar';
import {
    Container, Row, Col, Card, CardBody, Input, FormGroup, Label, CardHeader, Table,
    Badge
} from 'reactstrap';
import Select from 'react-select';
import Chart from 'react-apexcharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartBar, faChartLine, faCalendarAlt, faTint, faMicroscope, faFlask, faVial, faFilter
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { stringUtils } from '../_utils';

const TanqueDashboard = (props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedFazendas, setSelectedFazendas] = useState([]);
    const { tanque } = props.tanque;

    useEffect(() => {
        if (!authActions.isGestor()) {
            navigate('/home');
        }
    }, [navigate]);

    // Helper for elegant PT-BR formatting
    const formatBR = (val, precision = 2) => {
        if (val === undefined || val === null || isNaN(val)) return '0';
        return new Intl.NumberFormat('pt-BR', {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision
        }).format(val);
    };

    useEffect(() => {
        dispatch(tanqueAction.getTanqueByPeriodo(selectedYear, true));
        dispatch(fazendaAction.getFazendaSelect(true));
    }, [selectedYear, dispatch]);

    const handleYearChange = (e) => {
        setSelectedYear(e.target.value);
    };

    const handleFazendaChange = (selectedOptions) => {
        setSelectedFazendas(selectedOptions || []);
    };

    const years = [];
    const currentYear = new Date().getFullYear();
    for (let i = 0; i <= 10; i++) {
        years.push(currentYear - i);
    }

    const renderOptionsFazendas = () => {
        return props.fazenda?.fazenda?.map((p, i) => ({
            value: p.id,
            label: p.nome
        })) || [];
    };

    const processData = () => {
        const months = [
            'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
            'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
        ];

        const volumeData = Array(12).fill(0);
        const ccsData = Array(12).fill(0);
        const ufcData = Array(12).fill(0);
        const counts = Array(12).fill(0);

        const gorduraData = Array(12).fill(0);
        const proteinaData = Array(12).fill(0);
        const nulData = Array(12).fill(0);

        const farmMetrics = {};

        if (tanque && tanque.length > 0) {
            const filteredByYear = tanque.filter(item => moment(item.periodo).year() === parseInt(selectedYear));

            filteredByYear.forEach(item => {
                const date = moment(item.periodo);
                const month = date.month();

                // Build farm metrics for ALL farms first
                if (!farmMetrics[item.id_fazenda]) {
                    farmMetrics[item.id_fazenda] = {
                        nome: item.fazenda?.nome || 'Desconhecida',
                        totalVolume: 0,
                        totalCcs: 0,
                        totalUfc: 0,
                        totalVacas: 0,
                        totalMediaLitros: 0,
                        totalGordura: 0,
                        totalProteina: 0,
                        totalNul: 0,
                        count: 0
                    };
                }
                const farm = farmMetrics[item.id_fazenda];
                farm.totalVolume += item.volume;
                farm.totalCcs += item.ccs;
                farm.totalUfc += item.ufc;
                farm.totalVacas += item.vacas_lactacao;
                farm.totalMediaLitros += item.media_litros_dia;
                farm.totalGordura += item.gordura;
                farm.totalProteina += item.proteina;
                farm.totalNul += item.nul;
                farm.count += 1;

                // Accumulate month data ONLY for selected farms (or all if none selected)
                const farmIds = selectedFazendas.map(f => String(f.value));
                if (farmIds.length === 0 || farmIds.includes(String(item.id_fazenda))) {
                    volumeData[month] += item.volume;
                    ccsData[month] += item.ccs;
                    ufcData[month] += item.ufc;
                    gorduraData[month] += item.gordura;
                    proteinaData[month] += item.proteina;
                    nulData[month] += item.nul;
                    counts[month] += 1;
                }
            });
        }

        const avgCcs = ccsData.map((val, i) => counts[i] > 0 ? parseFloat((val / counts[i]).toFixed(2)) : 0);
        const avgUfc = ufcData.map((val, i) => counts[i] > 0 ? parseFloat((val / counts[i]).toFixed(2)) : 0);
        const avgGordura = gorduraData.map((val, i) => counts[i] > 0 ? parseFloat((val / counts[i]).toFixed(2)) : 0);
        const avgProteina = proteinaData.map((val, i) => counts[i] > 0 ? parseFloat((val / counts[i]).toFixed(2)) : 0);
        const avgNul = nulData.map((val, i) => counts[i] > 0 ? parseFloat((val / counts[i]).toFixed(2)) : 0);

        const globalFarmsList = Object.values(farmMetrics).map(farm => ({
            ...farm,
            avgCcs: farm.totalCcs / farm.count,
            avgUfc: farm.totalUfc / farm.count,
            avgVacas: farm.totalVacas / farm.count,
            avgMediaLitros: farm.totalMediaLitros / farm.count,
            avgGordura: farm.totalGordura / farm.count,
            avgProteina: farm.totalProteina / farm.count,
            avgNul: farm.totalNul / farm.count
        }));

        const top5Ccs = [...globalFarmsList].sort((a, b) => b.avgCcs - a.avgCcs).slice(0, 5);
        const top5Gordura = [...globalFarmsList].sort((a, b) => b.avgGordura - a.avgGordura).slice(0, 5);
        const top5Volume = [...globalFarmsList].sort((a, b) => b.totalVolume - a.totalVolume).slice(0, 5);
        const top5Produtividade = [...globalFarmsList].sort((a, b) => b.avgMediaLitros - a.avgMediaLitros).slice(0, 5);

        const farmIds = selectedFazendas.map(f => String(f.value));
        const farmsList = farmIds.length > 0
            ? globalFarmsList.filter(f => farmIds.includes(String(Object.keys(farmMetrics).find(key => farmMetrics[key].nome === f.nome))))
            : globalFarmsList;

        // Fix for farmsList filtering logic to use IDs correctly
        const farmsListFinal = farmIds.length > 0
            ? Object.keys(farmMetrics)
                .filter(id => farmIds.includes(String(id)))
                .map(id => {
                    const farm = farmMetrics[id];
                    return {
                        ...farm,
                        avgCcs: farm.totalCcs / farm.count,
                        avgUfc: farm.totalUfc / farm.count,
                        avgVacas: farm.totalVacas / farm.count,
                        avgMediaLitros: farm.totalMediaLitros / farm.count,
                        avgGordura: farm.totalGordura / farm.count,
                        avgProteina: farm.totalProteina / farm.count,
                        avgNul: farm.totalNul / farm.count
                    };
                })
            : globalFarmsList;

        return {
            months, volumeData, avgCcs, avgUfc, avgGordura, avgProteina, avgNul,
            farmsList: farmsListFinal.sort((a, b) => b.totalVolume - a.totalVolume),
            top5Ccs, top5Gordura, top5Volume, top5Produtividade
        };
    };

    const {
        months, volumeData, avgCcs, avgUfc, avgGordura, avgProteina, avgNul,
        farmsList, top5Ccs, top5Gordura, top5Volume, top5Produtividade
    } = processData();

    const volumeChartOptions = {
        chart: { id: 'volume-chart', toolbar: { show: false } },
        xaxis: { categories: months },
        yaxis: {
            labels: {
                formatter: (val) => {
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
                }
            }
        },
        tooltip: {
            y: {
                formatter: (val) => {
                    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
                }
            }
        },
        colors: ['#007bff'],
        title: { text: 'Volume Total por Mês', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4 }
    };

    const volumeChartSeries = [{ name: 'Volume', data: volumeData }];

    const qualityChartOptions = {
        chart: { id: 'quality-chart', toolbar: { show: false } },
        xaxis: { categories: months },
        colors: ['#28a745', '#ffc107'],
        title: { text: 'Qualidade (CCS e UFC)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        legend: { position: 'top' }
    };

    const qualityChartSeries = [
        { name: 'CCS Médio', data: avgCcs },
        { name: 'UFC Médio', data: avgUfc }
    ];

    return (
        <div style={{ backgroundColor: '#f4f7f6', minHeight: '100vh', paddingBottom: '80px' }}>
            <NavBar {...props} />
            <Container className="py-4">
                <Row className="mb-4 align-items-center">
                    <Col lg={5}>
                        <h2 className="font-weight-bold text-dark d-flex align-items-center">
                            <FontAwesomeIcon icon={faChartBar} className="mr-3 text-primary" />
                            Dashboard de Tanques
                        </h2>
                        <p className="text-muted">Análise consolidada de produção e qualidade</p>
                    </Col>
                    <Col lg={7}>
                        <Row>
                            <Col md={4} className="mb-2 mb-md-0">
                                <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                                    <CardBody className="py-2">
                                        <FormGroup className="mb-0">
                                            <Label className="small font-weight-bold text-muted mb-1">Ano</Label>
                                            <Input
                                                type="select"
                                                value={selectedYear}
                                                onChange={handleYearChange}
                                                style={{ borderRadius: '8px', border: '1px solid #eee' }}
                                            >
                                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                                            </Input>
                                        </FormGroup>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <Row>
                    <Col md={12} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <CardBody className="py-2">
                                <FormGroup className="mb-0">
                                    <Label className="small font-weight-bold text-muted mb-1">Filtrar Fazendas</Label>
                                    <Select
                                        isMulti
                                        options={renderOptionsFazendas()}
                                        value={selectedFazendas}
                                        onChange={handleFazendaChange}
                                        placeholder="Todas as fazendas..."
                                        classNamePrefix="select"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                borderRadius: '8px',
                                                border: '1px solid #eee',
                                                boxShadow: 'none'
                                            })
                                        }}
                                    />
                                </FormGroup>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    {/* Summary Cards Row 1 */}
                    <Col md={4} className="mb-4">
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div style={{ height: '4px', backgroundColor: '#007bff' }} />
                            <CardBody className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ backgroundColor: 'rgba(0,123,255,0.1)', width: '55px', height: '55px' }}>
                                    <FontAwesomeIcon icon={faTint} className="text-primary" size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small font-weight-bold text-uppercase">Volume Total</p>
                                    <h4 className="font-weight-bold mb-0">{formatBR(volumeData.reduce((a, b) => a + b, 0), 2)} L</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-4">
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div style={{ height: '4px', backgroundColor: '#dc3545' }} />
                            <CardBody className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ backgroundColor: 'rgba(220,53,69,0.1)', width: '55px', height: '55px' }}>
                                    <FontAwesomeIcon icon={faMicroscope} className="text-danger" size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small font-weight-bold text-uppercase">Média CCS</p>
                                    <h4 className="font-weight-bold mb-0">{formatBR(avgCcs.reduce((a, b) => a + b, 0) / (avgCcs.filter(v => v > 0).length || 1), 0)}</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-4">
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div style={{ height: '4px', backgroundColor: '#ffc107' }} />
                            <CardBody className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ backgroundColor: 'rgba(255,193,7,0.1)', width: '55px', height: '55px' }}>
                                    <FontAwesomeIcon icon={faFlask} className="text-warning" size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small font-weight-bold text-uppercase">Média UFC</p>
                                    <h4 className="font-weight-bold mb-0">{formatBR(avgUfc.reduce((a, b) => a + b, 0) / (avgUfc.filter(v => v > 0).length || 1), 0)}</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Summary Cards Row 2 - New Indicators */}
                    <Col md={4} className="mb-4">
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div style={{ height: '4px', backgroundColor: '#e83e8c' }} />
                            <CardBody className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ backgroundColor: 'rgba(232,62,140,0.1)', width: '55px', height: '55px' }}>
                                    <FontAwesomeIcon icon={faFlask} className="text-pink" style={{ color: '#e83e8c' }} size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small font-weight-bold text-uppercase">Média Gordura</p>
                                    <h4 className="font-weight-bold mb-0">{formatBR(avgGordura.reduce((a, b) => a + b, 0) / (avgGordura.filter(v => v > 0).length || 1))}%</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-4">
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div style={{ height: '4px', backgroundColor: '#20c997' }} />
                            <CardBody className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ backgroundColor: 'rgba(32,201,151,0.1)', width: '55px', height: '55px' }}>
                                    <FontAwesomeIcon icon={faFlask} className="text-teal" style={{ color: '#20c997' }} size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small font-weight-bold text-uppercase">Média Proteína</p>
                                    <h4 className="font-weight-bold mb-0">{formatBR(avgProteina.reduce((a, b) => a + b, 0) / (avgProteina.filter(v => v > 0).length || 1))}%</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} className="mb-4">
                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                            <div style={{ height: '4px', backgroundColor: '#fd7e14' }} />
                            <CardBody className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center mr-3" style={{ backgroundColor: 'rgba(253,126,20,0.1)', width: '55px', height: '55px' }}>
                                    <FontAwesomeIcon icon={faVial} className="text-orange" style={{ color: '#fd7e14' }} size="lg" />
                                </div>
                                <div>
                                    <p className="text-muted mb-0 small font-weight-bold text-uppercase">Média NUL</p>
                                    <h4 className="font-weight-bold mb-0">{formatBR(avgNul.reduce((a, b) => a + b, 0) / (avgNul.filter(v => v > 0).length || 1))}</h4>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col lg={8} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardHeader className="bg-transparent border-0 pt-4 px-4">
                                <h5 className="font-weight-bold text-dark mb-0">Produção Mensal</h5>
                            </CardHeader>
                            <CardBody className="px-4 pb-4">
                                <Chart
                                    options={volumeChartOptions}
                                    series={volumeChartSeries}
                                    type="line"
                                    height={300}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col lg={4} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardHeader className="bg-transparent border-0 pt-4 px-4">
                                <h5 className="font-weight-bold text-dark mb-0">Qualidade</h5>
                            </CardHeader>
                            <CardBody className="px-4 pb-4">
                                <Chart
                                    options={{
                                        ...qualityChartOptions,
                                        colors: ['#dc3545', '#ffc107']
                                    }}
                                    series={qualityChartSeries}
                                    type="bar"
                                    height={300}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col md={12} className="mb-4 text-center">
                        <hr />
                        <h4 className="font-weight-bold text-dark mt-4">Análise Comparativa entre Fazendas</h4>
                        <p className="text-muted">Comparação direta das métricas para o período selecionado</p>
                    </Col>

                    <Col lg={12} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'farm-composition', toolbar: { show: false } },
                                        xaxis: { categories: farmsList.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome) },
                                        title: { text: 'Composição do Leite (Gordura, Proteína e NUL)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#e83e8c', '#20c997', '#fd7e14'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: false } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 2) },
                                        legend: { position: 'top' },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 2)
                                            }
                                        }
                                    }}
                                    series={[
                                        { name: 'Gordura (%)', data: farmsList.map(f => parseFloat(f.avgGordura.toFixed(2))) },
                                        { name: 'Proteína (%)', data: farmsList.map(f => parseFloat(f.avgProteina.toFixed(2))) },
                                        { name: 'NUL', data: farmsList.map(f => parseFloat(f.avgNul.toFixed(2))) }
                                    ]}
                                    type="bar"
                                    height={400}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={12} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'farm-quality', toolbar: { show: false } },
                                        xaxis: { categories: farmsList.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome) },
                                        title: { text: 'Qualidade Média (CCS e UFC)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#dc3545', '#ffc107'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: false } },
                                        dataLabels: { enabled: false },
                                        legend: { position: 'top' }
                                    }}
                                    series={[
                                        { name: 'CCS', data: farmsList.map(f => Math.round(f.avgCcs)) },
                                        { name: 'UFC', data: farmsList.map(f => Math.round(f.avgUfc)) }
                                    ]}
                                    type="bar"
                                    height={400}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Row 1: Volume and Vacas */}
                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'farm-volume', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: farmsList.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 2) }
                                        },
                                        title: { text: 'Volume Total por Fazenda (L)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#007bff'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val)
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Volume', data: farmsList.map(f => Math.round(f.totalVolume)) }]}
                                    type="bar"
                                    height={Math.max(300, farmsList.length * 40)}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'farm-vacas', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: [...farmsList].sort((a, b) => b.avgVacas - a.avgVacas).map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 1) }
                                        },
                                        title: { text: 'Média de Vacas em Lactação', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#17a2b8'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 1) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 1)
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Vacas', data: [...farmsList].sort((a, b) => b.avgVacas - a.avgVacas).map(f => parseFloat(f.avgVacas.toFixed(1))) }]}
                                    type="bar"
                                    height={Math.max(300, farmsList.length * 40)}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    {/* Row 2: Produtividade and Qualidade */}
                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'farm-prod', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: [...farmsList].sort((a, b) => b.avgMediaLitros - a.avgMediaLitros).map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 2) }
                                        },
                                        title: { text: 'Produtividade Média (L/Dia)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#6f42c1'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 2) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 2)
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Média L/Dia', data: [...farmsList].sort((a, b) => b.avgMediaLitros - a.avgMediaLitros).map(f => parseFloat(f.avgMediaLitros.toFixed(2))) }]}
                                    type="bar"
                                    height={Math.max(300, farmsList.length * 40)}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    {/* Top Rankings Row */}
                    <Col md={12} className="mb-4 text-center mt-4">
                        <hr />
                        <h4 className="font-weight-bold text-dark mt-4">Rankings Globais do Ano</h4>
                        <p className="text-muted">As 5 propriedades com maiores índices no período selecionado (independente de filtros)</p>
                    </Col>

                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'top-ccs', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: top5Ccs.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 2) }
                                        },
                                        title: { text: 'Top 5 Fazendas - Maior CCS', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#dc3545'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 2) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 2)
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Média CCS', data: top5Ccs.map(f => f.avgCcs) }]}
                                    type="bar"
                                    height={300}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'top-gordura', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: top5Gordura.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 2) }
                                        },
                                        title: { text: 'Top 5 Fazendas - Maior Gordura (%)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#e83e8c'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 2) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 2) + '%'
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Média Gordura', data: top5Gordura.map(f => f.avgGordura) }]}
                                    type="bar"
                                    height={300}
                                />
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'top-volume', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: top5Volume.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 2) }
                                        },
                                        title: { text: 'Top 5 Fazendas - Maior Produção (L)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#007bff'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 2) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 2) + ' L'
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Volume Total', data: top5Volume.map(f => f.totalVolume) }]}
                                    type="bar"
                                    height={300}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                    <Col lg={6} className="mb-4">
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                            <CardBody className="p-4">
                                <Chart
                                    options={{
                                        chart: { id: 'top-produtividade', toolbar: { show: false } },
                                        xaxis: { 
                                            categories: top5Produtividade.map(f => f.nome.length > 20 ? f.nome.substring(0, 20) + '...' : f.nome),
                                            labels: { formatter: (val) => formatBR(val, 2) }
                                        },
                                        title: { text: 'Top 5 Fazendas - Maior Produtividade (L/Dia)', align: 'left', style: { fontSize: '14px', fontWeight: 'bold' } },
                                        colors: ['#6f42c1'],
                                        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
                                        dataLabels: { enabled: true, formatter: (val) => formatBR(val, 2) },
                                        tooltip: {
                                            y: {
                                                formatter: (val) => formatBR(val, 2)
                                            }
                                        }
                                    }}
                                    series={[{ name: 'Média L/Dia', data: top5Produtividade.map(f => f.avgMediaLitros) }]}
                                    type="bar"
                                    height={300}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        tanque: state.tanque,
        fazenda: state.fazenda
    };
};

const connectedTanqueDashboard = withRouter(connect(mapStateToProps)(TanqueDashboard));
export { connectedTanqueDashboard as TanqueDashboard };

